import type { FullComposeEntrySourceType } from 'owa-calendar-helpers-types/lib/ComposeMetricTypes';
import type { ClientFolderId } from 'owa-client-ids';
import type EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import { emailAddressWrapperToAttendeeType } from './emailAddressWrapperToAttendeeType';
import type EnhancedLocation from 'owa-service/lib/contract/EnhancedLocation';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type BodyContentType from 'owa-service/lib/contract/BodyContentType';
import {
    createEnhancedLocationsFromPersonaControlViewState,
    createLocationPersonaFromStringLocation,
} from 'owa-location-persona';
import { assumeDisplayDate, now } from 'owa-datetime';
import { launchCalendarFullCompose } from 'owa-calendar-forms-launch-handlers';

export function displayNewAppointmentFormApiHandler(
    entrySource: FullComposeEntrySourceType,
    parentFolderId?: ClientFolderId,
    requiredAttendees?: EmailAddressWrapper[],
    optionalAttendees?: EmailAddressWrapper[],
    start?: number,
    end?: number,
    location?: string,
    resources?: string[],
    subject?: string,
    body?: string
) {
    // Attendees
    const required = emailAddressWrapperToAttendeeType(requiredAttendees);
    const optional = emailAddressWrapperToAttendeeType(optionalAttendees);

    // Time

    // TZ_BUG(fpintos): OWA and Desktop Outlook behave differently when responding
    // to the add-in displayNewAppointmentForm call. Desktop Outlook is in
    // the same time zone as the OS, so the new event is created with the
    // face-values of the date in the same time zone expected by the add-in.
    // OWA, on the other hand, takes the face-values of the given Date object
    // and ends up creating the event in OWA's time zone, so it ends up not in
    // the same timestamp. According to the add-in team, this difference is
    // expected and explained here:
    // https://docs.microsoft.com/en-us/office/dev/add-ins/reference/objectmodel/requirement-set-1.7/office.context.mailbox#converttolocalclienttimetimevalue--localclienttimejavascriptapioutlook17officelocalclienttime
    // What this means for us is that we should assume the Date to be a display date.

    const startTime = start ? assumeDisplayDate(new Date(start))! : now();
    const endTime = end ? assumeDisplayDate(new Date(end))! : undefined;

    // Location
    let locations: EnhancedLocation[] | undefined = undefined;
    if (location) {
        const viewstate = createLocationPersonaFromStringLocation(location);
        const enhancedLocation = createEnhancedLocationsFromPersonaControlViewState(viewstate)[0];

        if (enhancedLocation) {
            locations = [enhancedLocation];
        }
    }

    //  Resources
    let resourceAttendees: AttendeeType[] | undefined = undefined;
    if (resources) {
        const filteredResources = resources.filter(resource => !!resource);

        if (filteredResources?.length > 0) {
            resourceAttendees = filteredResources.map(res => {
                return <AttendeeType>{
                    Mailbox: { EmailAddress: res },
                };
            });
        }
    }

    // Body
    let bodyContent: BodyContentType | undefined = undefined;
    if (body) {
        bodyContent = {
            Value: body,
            BodyType: 'HTML',
        };
    }

    launchCalendarFullCompose({
        fullComposeEntrySource: entrySource,
        eventUpdates: {
            ParentFolderId: parentFolderId,
            RequiredAttendees: required,
            OptionalAttendees: optional,
            Start: startTime,
            End: endTime,
            Locations: locations,
            Resources: resourceAttendees,
            Subject: subject,
            Body: bodyContent,
        },
    });
}
